import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const BUNDLE_STATE_EVENT = 'bundle:state-change';
const DEFAULT_CURRENCY_DECIMALS = 2;
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XCD: 0,
  XDR: 0,
  XOF: 0,
  XPF: 0,
  YER: 0
};

/**
 * @typedef {{active: boolean, valid: boolean, selectedItems: Array<{id: number, quantity: number}>}} BundleState
 *
 * @typedef {Object} ProductBundleRefs
 * @property {HTMLInputElement} masterToggle
 * @property {HTMLElement} originalPrice
 * @property {HTMLElement} discountedPrice
 * @property {HTMLElement} savingsPrice
 * @property {HTMLElement} statusMessage
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} mainProductTitle
 * @property {HTMLElement} mainVariantTitle
 * @property {HTMLElement} mainCurrentPrice
 * @property {HTMLElement} mainComparePrice
 * @property {HTMLTemplateElement} moneyFormat
 */
class ProductBundleComponent extends Component {
  requiredRefs = [
    'masterToggle',
    'originalPrice',
    'discountedPrice',
    'savingsPrice',
    'statusMessage',
    'errorMessage',
    'mainProductTitle',
    'mainVariantTitle',
    'mainCurrentPrice',
    'mainComparePrice',
    'moneyFormat'
  ];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section, dialog');

    this.addEventListener('change', this.#handleChange, { signal });
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.syncState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @returns {BundleState}
   */
  getBundleState() {
    const selectedItems = this.#selectedCompanionRows().map((row) => ({
      id: Number(row.dataset.variantId),
      quantity: 1
    }));
    const active = this.refs.masterToggle.checked && selectedItems.length > 0;
    const valid = !active || this.#selectedCompanionRows().every((row) => row.dataset.available === 'true');

    return { active, valid, selectedItems };
  }

  syncState() {
    const enabled = this.refs.masterToggle.checked;
    const bundleState = this.getBundleState();
    const mainPrice = Number(this.dataset.mainPrice || 0);
    const mainCompareAtPrice = Number(this.dataset.mainCompareAtPrice || mainPrice);
    const discountPercent = Number(this.dataset.bundleDiscountPercent || 0);

    this.dataset.bundleEnabled = enabled ? 'true' : 'false';
    this.dataset.bundleActive = bundleState.active ? 'true' : 'false';

    for (const checkbox of this.querySelectorAll('.product-bundle__item-checkbox')) {
      if (checkbox instanceof HTMLInputElement) {
        checkbox.disabled = !enabled;
      }
    }

    let originalTotal = mainPrice;
    let discountedTotal = mainPrice;
    let savingsTotal = 0;

    if (bundleState.active) {
      const selectedRows = this.#selectedCompanionRows();
      const subtotal = mainPrice + selectedRows.reduce((sum, row) => sum + Number(row.dataset.price || 0), 0);
      originalTotal =
        mainCompareAtPrice +
        selectedRows.reduce((sum, row) => sum + Number(row.dataset.compareAtPrice || row.dataset.price || 0), 0);
      discountedTotal = Math.round(subtotal * ((100 - discountPercent) / 100));
      savingsTotal = Math.max(originalTotal - discountedTotal, 0);
    }

    this.refs.originalPrice.textContent = this.#formatMoney(originalTotal);
    this.refs.discountedPrice.textContent = this.#formatMoney(discountedTotal);
    this.refs.savingsPrice.textContent = this.#formatMoney(savingsTotal);

    if (bundleState.active && bundleState.valid) {
      this.refs.statusMessage.textContent = 'Bundle selected. Your Add to Cart will include every checked item.';
      this.refs.errorMessage.classList.add('hidden');
    } else if (bundleState.active && !bundleState.valid) {
      this.refs.statusMessage.textContent = 'Bundle selected, but one item needs attention before checkout.';
      this.refs.errorMessage.classList.remove('hidden');
    } else if (enabled) {
      this.refs.statusMessage.textContent = 'Select at least one companion product to unlock the bundle price.';
      this.refs.errorMessage.classList.add('hidden');
    } else {
      this.refs.statusMessage.textContent = 'Bundle off. Add this item only.';
      this.refs.errorMessage.classList.add('hidden');
    }

    this.dispatchEvent(
      new CustomEvent(BUNDLE_STATE_EVENT, {
        bubbles: true,
        detail: bundleState
      })
    );
  }

  #handleChange = () => {
    this.syncState();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      const newBundle = event.detail.data.html.querySelector(
        `product-bundle-component[data-block-id="${this.dataset.blockId}"]`
      );

      if (newBundle instanceof HTMLElement) {
        this.dataset.productId = newBundle.dataset.productId || this.dataset.productId;
        this.dataset.mainProductTitle = newBundle.dataset.mainProductTitle || this.dataset.mainProductTitle;
        this.refs.mainProductTitle.textContent = newBundle.dataset.mainProductTitle || this.refs.mainProductTitle.textContent;

        const newImage = newBundle.querySelector('.product-bundle__item--main .product-bundle__image');
        const currentImage = this.querySelector('.product-bundle__item--main .product-bundle__image');
        if (newImage instanceof HTMLImageElement && currentImage instanceof HTMLImageElement) {
          currentImage.src = newImage.src;
          currentImage.srcset = newImage.srcset;
          currentImage.alt = newImage.alt;
        }
      }
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;
    if (!variant) {
      this.dataset.mainAvailable = 'false';
      this.syncState();
      return;
    }

    this.dataset.mainPrice = String(variant.price ?? this.dataset.mainPrice ?? 0);
    this.dataset.mainCompareAtPrice = String(variant.compare_at_price ?? variant.price ?? this.dataset.mainCompareAtPrice ?? 0);
    this.dataset.mainAvailable = String(variant.available === true);

    this.refs.mainCurrentPrice.textContent = this.#formatMoney(Number(this.dataset.mainPrice || 0));

    const compareAtPrice = Number(this.dataset.mainCompareAtPrice || this.dataset.mainPrice || 0);
    if (compareAtPrice > Number(this.dataset.mainPrice || 0)) {
      this.refs.mainComparePrice.hidden = false;
      this.refs.mainComparePrice.textContent = this.#formatMoney(compareAtPrice);
    } else {
      this.refs.mainComparePrice.hidden = true;
      this.refs.mainComparePrice.textContent = '';
    }

    const variantTitle = variant.title || '';
    if (variantTitle && variantTitle !== this.dataset.defaultVariantTitle) {
      this.refs.mainVariantTitle.hidden = false;
      this.refs.mainVariantTitle.textContent = variantTitle;
    } else {
      this.refs.mainVariantTitle.hidden = true;
      this.refs.mainVariantTitle.textContent = '';
    }

    const mainImage = this.querySelector('.product-bundle__item--main .product-bundle__image');
    const newImageSrc = variant.featured_media?.preview_image?.src;
    if (mainImage instanceof HTMLImageElement && newImageSrc) {
      mainImage.src = `${newImageSrc}&width=160`;
    }

    this.syncState();
  };

  /**
   * @returns {HTMLElement[]}
   */
  #selectedCompanionRows() {
    return Array.from(this.querySelectorAll('[data-bundle-row]')).filter((row) => {
      const checkbox = row.querySelector('.product-bundle__item-checkbox');
      return checkbox instanceof HTMLInputElement && checkbox.checked;
    });
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const template = this.refs.moneyFormat.content.textContent || '${{amount}}';
    const currency = (window?.Shopify?.currency?.active || '').toUpperCase();

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(cents, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * @param {number} cents
   * @param {string} thousandsSeparator
   * @param {string} decimalSeparator
   * @param {number} precision
   * @returns {string}
   */
  #formatCents(cents, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (cents / 100).toFixed(precision);
    let [whole, fraction] = roundedNumber.split('.');

    if (!whole) whole = '0';
    if (!fraction) fraction = '';

    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : `${whole}${decimalSeparator}${fraction.padEnd(precision, '0')}`;
  }
}

if (!customElements.get('product-bundle-component')) {
  customElements.define('product-bundle-component', ProductBundleComponent);
}
